/**
 * 🏢 Tenant Context - 멀티테넌트 지원을 위한 React Context
 *
 * 🎯 핵심 철학:
 * - 모든 컴포넌트에서 테넌트 ID 접근 가능
 * - 테넌트별 데이터 격리 보장
 * - 동적 테넌트 전환 지원
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getSupabase } from '../services/supabaseClient';
import { logger } from '../../../../shared/logger';
import { mapRowToTenantInfo, type TenantInfo, type TenantRow } from './tenantRowMapping';

export type { TenantInfo };

export interface TenantContextType {
  currentTenant: TenantInfo | null;
  availableTenants: TenantInfo[];
  switchTenant: (tenantId: string) => Promise<void>;
  refreshTenantInfo: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

function isNotFoundError(code: string | undefined): boolean {
  return code === 'PGRST116';
}

// ============================================
// 🏢 Tenant Context 생성
// ============================================

const TenantContext = createContext<TenantContextType | undefined>(undefined);

// ============================================
// 🏢 Tenant Provider 컴포넌트
// ============================================

interface TenantProviderProps {
  children: ReactNode;
  initialTenantId?: string;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({
  children,
  initialTenantId = 'default_tenant',
}) => {
  const [currentTenant, setCurrentTenant] = useState<TenantInfo | null>(null);
  const [availableTenants, setAvailableTenants] = useState<TenantInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ============================================
  // 🎯 테넌트 정보 로드 (Supabase)
  // ============================================

  const loadTenantInfo = async (tenantId: string): Promise<TenantInfo | null> => {
    try {
      const supabase = getSupabase();

      const { data, error } = await supabase
        .from('tenants')
        .select('*')
        .eq('tenant_id', tenantId)
        .maybeSingle();

      if (error) {
        if (isNotFoundError(error.code)) {
          logger.warn('⚠️ Supabase tenants 테이블에 테넌트 없음', { tenantId });
          return null;
        }
        logger.error('❌ Supabase 테넌트 로드 오류', undefined, {
          tenantId,
          code: error.code,
          message: error.message,
        });
        return null;
      }

      if (!data) {
        logger.warn('⚠️ tenants 행 없음', { tenantId });
        return null;
      }

      const tenantInfo = mapRowToTenantInfo(data as TenantRow);
      logger.debug('✅ Supabase에서 테넌트 정보 로드 성공:', { data: tenantInfo });
      return tenantInfo;
    } catch (err) {
      logger.error('❌ 테넌트 정보 로드 실패:', err instanceof Error ? err : undefined, {
        tenantId,
        raw: err,
      });
      return null;
    }
  };

  // ============================================
  // 🎯 사용 가능한 테넌트 목록 로드 (Supabase)
  // ============================================

  const loadAvailableTenants = async (): Promise<TenantInfo[]> => {
    try {
      const supabase = getSupabase();

      const { data, error } = await supabase.from('tenants').select('*');

      if (error) {
        logger.error('❌ Supabase 테넌트 목록 로드 오류', undefined, {
          code: error.code,
          message: error.message,
        });
        return [];
      }

      if (!data || data.length === 0) {
        logger.warn('⚠️ Supabase tenants 목록 비어 있음');
        return [];
      }

      const tenants = (data as TenantRow[]).map(mapRowToTenantInfo);
      logger.debug('✅ Supabase에서 테넌트 목록 로드 성공', {
        count: tenants.length,
        unit: '개',
      });
      return tenants;
    } catch (err) {
      logger.error('❌ 테넌트 목록 로드 실패:', err instanceof Error ? err : undefined, {
        raw: err,
      });
      return [];
    }
  };

  // ============================================
  // 🎯 테넌트 전환
  // ============================================

  const switchTenant = async (tenantId: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const tenantInfo = await loadTenantInfo(tenantId);

      if (!tenantInfo) {
        logger.warn('⚠️ DB 미등록 테넌트', { tenantId });
        setCurrentTenant(null);
        setError('TENANT_NOT_FOUND');
        return;
      }

      setCurrentTenant(tenantInfo);
      localStorage.setItem('currentTenantId', tenantInfo.id);
      logger.debug(`✅ 테넌트 전환 완료: ${tenantInfo.id}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '테넌트 전환 실패';
      setCurrentTenant(null);
      setError(`LOAD_ERROR: ${errorMessage}`);
      logger.error('테넌트 전환 실패:', err instanceof Error ? err : undefined, {
        raw: err,
      });
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // 🎯 테넌트 정보 새로고침
  // ============================================

  const refreshTenantInfo = async (): Promise<void> => {
    if (currentTenant) {
      await switchTenant(currentTenant.id);
    }
  };

  // ============================================
  // 🎯 초기화
  // ============================================

  useEffect(() => {
    const initializeTenant = async () => {
      try {
        setLoading(true);
        setError(null);

        const tenants = await loadAvailableTenants();
        setAvailableTenants(tenants);

        // hostname(initialTenantId)가 SSOT — localStorage는 동일 tenant 재방문용
        const savedTenantId = localStorage.getItem('currentTenantId');
        const tenantIdToLoad = initialTenantId || savedTenantId || 'default_tenant';
        await switchTenant(tenantIdToLoad);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '테넌트 초기화 실패';
        setCurrentTenant(null);
        setError(`LOAD_ERROR: ${errorMessage}`);
        logger.error('테넌트 초기화 실패:', err instanceof Error ? err : undefined, {
          raw: err,
        });
      } finally {
        setLoading(false);
      }
    };

    initializeTenant();
  }, [initialTenantId]);

  // ============================================
  // 🎯 Context 값
  // ============================================

  const contextValue: TenantContextType = {
    currentTenant,
    availableTenants,
    switchTenant,
    refreshTenantInfo,
    loading,
    error,
  };

  return <TenantContext.Provider value={contextValue}>{children}</TenantContext.Provider>;
};

// ============================================
// 🎯 Custom Hook
// ============================================

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

// ============================================
// 🎯 HOC for Tenant-aware Components
// ============================================

export const withTenant = <P extends object>(
  Component: React.ComponentType<P & { tenantId: string }>
) => {
  return (props: P) => {
    const { currentTenant } = useTenant();

    if (!currentTenant) {
      return <div>테넌트를 로딩 중입니다...</div>;
    }

    return <Component {...props} tenantId={currentTenant.id} />;
  };
};

export default TenantContext;
